import { languages } from "@consts/cconsts";
import { Dialog, DialogPanel } from "@headlessui/react";
import {
  Bars3Icon,
  ChevronLeftIcon,
  ChevronRightIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { AnimatePresence, motion } from "framer-motion";
import Image from "next/image";
import Link from "next/link";
import useTranslation from "next-translate/useTranslation";
import { useState } from "react";

function classNames(...classes: unknown[]) {
  return classes.filter(Boolean).join(" ");
}

interface MenuItem {
  href: string;
  isNew?: boolean;
  name: string;
}

interface Subgroup {
  items: MenuItem[];
  name: string;
}

interface Group {
  items?: MenuItem[];
  name: string;
  subgroups?: Subgroup[];
}

interface Category {
  groups: Group[];
  id: string;
  name: string;
}

type MobileView =
  | "main"
  | "products"
  | "category"
  | "group"
  | "downloads"
  | "locales";

export const MobileMenu = () => {
  const { t, lang } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<MobileView>("main");
  const [direction, setDirection] = useState(1);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(
    null
  );
  const [selectedGroupIndex, setSelectedGroupIndex] = useState<number | null>(
    null
  );

  const closeMenu = () => {
    setIsOpen(false);
    setTimeout(() => {
      setView("main");
      setSelectedCategoryId(null);
      setSelectedGroupIndex(null);
    }, 300);
  };

  const goTo = (newView: MobileView) => {
    setDirection(1);
    setView(newView);
  };

  const goBack = () => {
    setDirection(-1);
    if (view === "group") {
      const cat = productCategories.find((c) => c.id === selectedCategoryId);
      setView(cat && cat.groups.length === 1 ? "products" : "category");
    } else if (view === "category") {
      setView("products");
    } else {
      setView("main");
    }
  };

  const selectCategory = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    const cat = productCategories.find((c) => c.id === categoryId);
    if (cat && cat.groups.length === 1) {
      setSelectedGroupIndex(0);
      goTo("group");
    } else {
      goTo("category");
    }
  };

  const selectGroup = (groupIndex: number) => {
    setSelectedGroupIndex(groupIndex);
    goTo("group");
  };

  const productCategories: Category[] = [
    {
      groups: [
        {
          name: "OKNA PVC",
          subgroups: [
            {
              items: [
                {
                  href: "/pvc/windows/veka/pure-82-2-7456",
                  isNew: true,
                  name: "PURE 82+",
                },
                {
                  href: "/pvc/windows/veka/pure-82-7434",
                  isNew: true,
                  name: "PURE 82",
                },
                {
                  href: "/pvc/windows/veka/softline-82-md-36",
                  name: "SOFTLINE 82 MD",
                },
                {
                  href: "/pvc/windows/veka/softline-76-md-72",
                  name: "SOFTLINE 76 MD",
                },
                {
                  href: "/pvc/windows/veka/perfectline-80",
                  name: "PERFECTLINE",
                },
                {
                  href: "/pvc/windows/veka/perfectline-zaokraglony-606",
                  name: "PERFECTLINE ZAOKRĄGLONY",
                },
              ],
              name: "VEKA",
            },
            {
              items: [
                {
                  href: "/pvc/windows/gealan/linear-geo-7359",
                  isNew: true,
                  name: "LINEAR GEO",
                },
                {
                  href: "/pvc/windows/gealan/gealan-kontur-7008",
                  name: "GEALAN KONTUR",
                },
                {
                  href: "/pvc/windows/gealan/gealan-acrylcolor-4674",
                  name: "GEALAN ACRYLCOLOR",
                },
                {
                  href: "/pvc/windows/gealan/gealan-stv-3847",
                  name: "GEALAN STV",
                },
                { href: "/pvc/windows/gealan/linear-307", name: "LINEAR" },
                { href: "/pvc/windows/gealan/optiline-304", name: "OPTILINE" },
                {
                  href: "/pvc/windows/gealan/optiline-plus-301",
                  name: "OPTILINE PLUS",
                },
                { href: "/pvc/windows/gealan/termoline-55", name: "TERMOLINE" },
              ],
              name: "GEALAN",
            },
            {
              items: [
                {
                  href: "/pvc/windows/decco/decco-70-ad-6954",
                  name: "DECCO 70 AD",
                },
                {
                  href: "/pvc/windows/decco/decco-84-md-6949",
                  name: "DECCO 84 MD",
                },
              ],
              name: "DECCO",
            },
            {
              items: [
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76-flexa-7336",
                  isNew: true,
                  name: "IDEAL NEO 76 FLEXA",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-8000-7046",
                  name: "IDEAL 8000",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-4000-6419",
                  name: "IDEAL 4000",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76ad-6041",
                  name: "IDEAL NEO 76 AD",
                },
                {
                  href: "/pvc/windows/aluplast/ideal-neo-76md-6036",
                  name: "IDEAL NEO 76 MD",
                },
                {
                  href: "/pvc/windows/aluplast/intertec-85md-6029",
                  name: "INTERTEC 85 MD",
                },
              ],
              name: "ALUPLAST",
            },
          ],
        },
        {
          name: "OKNA ALU",
          subgroups: [
            ...(lang === "pl"
              ? [
                  {
                    items: [
                      {
                        href: "/alu/windows/aluplast/one-75hi-7614",
                        isNew: true,
                        name: "ONE 75HI",
                      },
                    ],
                    name: "ALUPLAST",
                  },
                ]
              : []),
            {
              items: [
                {
                  href: "/alu/windows/aluprof/mb-104-passive-816",
                  name: "MB 104 PASSIVE",
                },
                { href: "/alu/windows/aluprof/mb-45-716", name: "MB 45" },
                { href: "/alu/windows/aluprof/mb-60-775", name: "MB 60" },
                { href: "/alu/windows/aluprof/mb-70-266", name: "MB 70" },
                { href: "/alu/windows/aluprof/mb-79n-806", name: "MB 79N" },
                { href: "/alu/windows/aluprof/mb-86ei-819", name: "MB 86EI" },
                { href: "/alu/windows/aluprof/mb-86n-813", name: "MB 86N" },
                {
                  href: "/alu/windows/aluprof/mb-ferroline-825",
                  name: "MB FERROLINE",
                },
                {
                  href: "/alu/windows/aluprof/mb-slimline-822",
                  name: "MB SLIMLINE",
                },
              ],
              name: "ALUPROF",
            },
            {
              items: [
                { href: "/alu/windows/aluron/as-75-834", name: "AS 75" },
                { href: "/alu/windows/aluron/as-80us-841", name: "AS 80US" },
                { href: "/alu/windows/aluron/as-110-838", name: "AS 110" },
              ],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/windows/reynaers/masterline-8-828",
                  name: "MASTERLINE 8",
                },
                {
                  href: "/alu/windows/reynaers/masterline-10-831",
                  name: "MASTERLINE 10",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "okna",
      name: t("pvc.windows").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            { href: "/pvc/doors/drzwi-wejsciowe-313", name: "DRZWI WEJŚCIOWE" },
            {
              href: "/pvc/doors/drzwi-nakladkowe-311",
              name: "DRZWI NAKŁADKOWE",
            },
            { href: "/pvc/doors/drzwi-werandowe-309", name: "DRZWI WERANDOWE" },
          ],
          name: "DRZWI PVC",
        },
        {
          name: "DRZWI ALU",
          subgroups: [
            {
              items: [
                {
                  href: "/alu/doors/aluprof/mb-104-1144",
                  name: "MB 104 PASSIVE AERO",
                },
                { href: "/alu/doors/aluprof/mb-45-1164", name: "MB 45" },
                { href: "/alu/doors/aluprof/mb-60-1161", name: "MB 60" },
                {
                  href: "/alu/doors/aluprof/mb-60e-ei-1141",
                  name: "MB 60E EI",
                },
                { href: "/alu/doors/aluprof/mb-70-1158", name: "MB 70 HI" },
                { href: "/alu/doors/aluprof/mb-78ei-1137", name: "MB 78EI" },
                { href: "/alu/doors/aluprof/mb-79n-1154", name: "MB 79N" },
                { href: "/alu/doors/aluprof/mb-86n-1147", name: "MB 86N" },
                { href: "/alu/doors/aluprof/mb-86st-1150", name: "MB 86ST" },
                { href: "/alu/doors/aluprof/mb-86ei-1133", name: "MB-86EI" },
              ],
              name: "ALUPROF",
            },
            {
              items: [{ href: "/alu/doors/aluron/as-75-1125", name: "AS 75G" }],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/doors/reynaers/masterline-8-1131",
                  name: "MASTERLINE 8",
                },
                {
                  href: "/alu/doors/reynaers/masterline-8-pivot-1127",
                  name: "MASTERLINE 8 PIVOT",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "drzwi",
      name: t("pvc.doors").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            { href: "/alu/multilevel/multieco/me-000-1573", name: "ME 000" },
            { href: "/alu/multilevel/multieco/me-001-1598", name: "ME 001" },
            {
              href: "/alu/multilevel/multieco/me-001nx-1601",
              name: "ME 001NX",
            },
            { href: "/alu/multilevel/multieco/me-002-1604", name: "ME 002" },
            { href: "/alu/multilevel/multieco/me-003-1607", name: "ME 003" },
            {
              href: "/alu/multilevel/multieco/me-003nx-1610",
              name: "ME 003NX",
            },
            { href: "/alu/multilevel/multieco/me-004-1613", name: "ME 004" },
            {
              href: "/alu/multilevel/multieco/me-004nx-1616",
              name: "ME 004NX",
            },
            { href: "/alu/multilevel/multieco/me-008-1619", name: "ME 008" },
            {
              href: "/alu/multilevel/multieco/me-008nx-1622",
              name: "ME 008NX",
            },
            { href: "/alu/multilevel/multieco/me-011-1625", name: "ME 011" },
            {
              href: "/alu/multilevel/multieco/me-011nx-1628",
              name: "ME 011NX",
            },
            { href: "/alu/multilevel/multieco/me-021-1631", name: "ME 021" },
            {
              href: "/alu/multilevel/multieco/me-021nx-1636",
              name: "ME 021NX",
            },
            { href: "/alu/multilevel/multieco/me-211-1643", name: "ME 211" },
            {
              href: "/alu/multilevel/multieco/me-211nx-1650",
              name: "ME 211NX",
            },
          ],
          name: "MULTIECO",
        },
        {
          items: [
            {
              href: "/alu/multilevel/multilevel",
              name: "KOMFORT NA WEJŚCIU (KN 001)",
            },
            { href: "/alu/multilevel/multilevel", name: "RETRO LINE (KN 038)" },
            { href: "/alu/multilevel/multilevel", name: "STONE (KN 0101)" },
            { href: "/alu/multilevel/multilevel", name: "BETON (KN 0300)" },
            { href: "/alu/multilevel/multilevel", name: "LOFT (KN 0400)" },
            { href: "/alu/multilevel/multilevel", name: "NATURAL (KN 0600)" },
          ],
          name: "MULTILEVEL",
        },
        {
          items: [
            {
              href: "/alu/multilevel/technology/ihorizon-exclusive-5226",
              name: "IHORIZON EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/ihorizon-exclusive-plus-5214",
              name: "IHORIZON EXCLUSIVE+",
            },
            {
              href: "/alu/multilevel/technology/mb-79n-exclusive-5217",
              name: "MB-79N EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/mb-86n-exclusive-5221",
              name: "MB-86N EXCLUSIVE",
            },
            {
              href: "/alu/multilevel/technology/mb-86n-exclusive-plus-5207",
              name: "MB-86N EXCLUSIVE+",
            },
            {
              href: "/alu/multilevel/technology/multieco-2-1187",
              name: "MULTIECO",
            },
          ],
          name: "TECHNOLOGY",
        },
      ],
      id: "multilevel",
      name: "MULTILEVEL",
    },
    {
      groups: [
        {
          name: "DRZWI PRZESUWNE PVC",
          subgroups: [
            {
              items: [
                {
                  href: "/pvc/sliding-doors/veka/vekamove-76-5106",
                  name: "VEKAMOVE 76",
                },
                {
                  href: "/pvc/sliding-doors/veka/vekamotion-82-476",
                  name: "VEKAMOTION 82",
                },
              ],
              name: "VEKA",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/gealan/hst-gealan-5108",
                  name: "HST GEALAN",
                },
                {
                  href: "/pvc/sliding-doors/gealan/hs-inowa-480",
                  name: "HS INOWA",
                },
              ],
              name: "GEALAN",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/decco/decco-hst-100-472",
                  name: "DECCO HST 100",
                },
              ],
              name: "DECCO",
            },
            {
              items: [
                {
                  href: "/pvc/sliding-doors/aluplast/smart-slide-neo-6659",
                  name: "SMART-SLIDE NEO",
                },
                {
                  href: "/pvc/sliding-doors/aluplast/hst-85-6656",
                  name: "HST 85",
                },
              ],
              name: "ALUPLAST",
            },
          ],
        },
        {
          name: "DRZWI PRZESUWNE ALU",
          subgroups: [
            {
              items: [
                {
                  href: "/alu/sliding-doors/aluprof/mb-59-galandage-865",
                  name: "MB 59 GALANDAGE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-59-slide-861",
                  name: "MB 59 SLIDE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-59hs-856",
                  name: "MB 59HS",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-77hs-868",
                  name: "MB 77HS",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-86-foldline-878",
                  name: "MB 86 FOLDLINE",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-skyline-type-r-873",
                  name: "MB SKYLINE TYPE R",
                },
                {
                  href: "/alu/sliding-doors/aluprof/mb-slide-854",
                  name: "MB SLIDE",
                },
              ],
              name: "ALUPROF",
            },
            {
              items: [
                {
                  href: "/alu/sliding-doors/aluron/aluron-as-178hs-891",
                  name: "AS 178HS",
                },
                {
                  href: "/alu/sliding-doors/aluron/as-178hs-modern-7427",
                  name: "AS 178HS MODERN",
                },
              ],
              name: "ALURON",
            },
            {
              items: [
                {
                  href: "/alu/sliding-doors/reynaers/masterpatio-881",
                  name: "MASTERPATIO",
                },
              ],
              name: "REYNAERS",
            },
          ],
        },
      ],
      id: "systemy",
      name: t("pvc.sliding-doors").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/adaptive",
              name: t("rollers.adaptive").toUpperCase(),
            },
            {
              href: "/rollers/concealed",
              name: t("rollers.concealed").toUpperCase(),
            },
            {
              href: "/rollers/for-windows",
              name: t("rollers.for-windows").toUpperCase(),
            },
            {
              href: "/rollers/lamella",
              name: t("rollers.lamella").toUpperCase(),
            },
          ],
          name: t("rollers.title").toUpperCase(),
        },
      ],
      id: "rolety",
      name: t("rollers.title").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/blinds",
              name: t("rollers.blinds").toUpperCase(),
            },
          ],
          name: "ŻALUZJE",
        },
      ],
      id: "zaluzje",
      name: "ŻALUZJE FASADOWE",
    },
    {
      groups: [
        {
          items: [
            {
              href: "/rollers/screens",
              name: t("rollers.screens").toUpperCase(),
            },
          ],
          name: "MOSKITIERY",
        },
      ],
      id: "moskitiery",
      name: "MOSKITIERY",
    },
    {
      groups: [
        {
          items: [
            {
              href: "/alu/facade-systems",
              name: t("alu.facadeSystems").toUpperCase(),
            },
          ],
          name: "FASADY",
        },
      ],
      id: "fasady",
      name: "FASADY",
    },
    {
      groups: [
        {
          items: [
            { href: "/pvc/accessories", name: "AKCESORIA PVC" },
            { href: "/alu/accessories", name: "AKCESORIA ALU" },
          ],
          name: "POZOSTAŁE",
        },
      ],
      id: "dodatki",
      name: "DODATKI",
    },
    {
      groups: [
        {
          items: [
            { href: "/pergolas/mb-opensky-140-3903", name: "MB-OpenSky 140" },
            {
              href: "/pergolas/mb-opensky-120-nowosc-3905",
              name: "MB-OpenSky 120",
            },
          ],
          name: t("pergolas.title").toUpperCase(),
        },
      ],
      id: "pergola",
      name: t("pergolas.title").toUpperCase(),
    },
    {
      groups: [
        {
          items: [
            { href: "/partners", name: t("menu.partners").toUpperCase() },
            { href: "/documents", name: t("menu.downloads").toUpperCase() },
          ],
          name: "DLA PARTNERÓW",
        },
      ],
      id: "materialy",
      name: "DOKUMENTY",
    },
  ];

  const variants = {
    center: { opacity: 1, x: 0 },
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? "100%" : "-100%",
    }),
    exit: (dir: number) => ({
      opacity: 0,
      x: dir < 0 ? "100%" : "-100%",
    }),
  };

  const selectedCategory = selectedCategoryId
    ? (productCategories.find((c) => c.id === selectedCategoryId) ?? null)
    : null;

  const selectedGroup =
    selectedCategory && selectedGroupIndex !== null
      ? (selectedCategory.groups[selectedGroupIndex] ?? null)
      : null;

  const slideProps = {
    animate: "center" as const,
    exit: "exit" as const,
    initial: "enter" as const,
    transition: { duration: 0.25, type: "tween" as const },
    variants,
  };

  const handleOpenMenu = () => setIsOpen(true);
  const handleGoToProducts = () => goTo("products");
  const handleGoToDownloads = () => goTo("downloads");
  const handleGoToLocales = () => goTo("locales");

  return (
    <div className="md:hidden">
      {/* Burger Button */}
      <div className="flex items-center justify-between px-4 py-3">
        <Link href="/">
          <Image
            alt="KNS"
            className="h-10 w-auto"
            height={40}
            src="/logo/kns-gold-pl.png"
            width={80}
          />
        </Link>
        <button
          className="rounded-md p-2 text-white hover:bg-white/10"
          onClick={handleOpenMenu}
          type="button"
        >
          <Bars3Icon className="h-8 w-8" />
        </button>
      </div>

      <Dialog className="relative z-[100]" onClose={closeMenu} open={isOpen}>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <DialogPanel className="pointer-events-auto w-screen max-w-md transform transition duration-500 ease-in-out">
                <div className="flex h-full flex-col bg-[#051124] text-white shadow-xl">
                  {/* Header */}
                  <div className="flex shrink-0 items-center justify-between border-white/10 border-b p-4">
                    <Image
                      alt="KNS"
                      className="h-10 w-auto"
                      height={40}
                      src="/logo/kns-gold-pl.png"
                      width={80}
                    />
                    <button
                      className="rounded-md p-2 text-white/70 hover:text-white"
                      onClick={closeMenu}
                      type="button"
                    >
                      <XMarkIcon className="h-7 w-7" />
                    </button>
                  </div>

                  {/* Drill-down content */}
                  <div className="relative min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
                    <AnimatePresence
                      custom={direction}
                      initial={false}
                      mode="wait"
                    >
                      {/* ── Level 1: Main ── */}
                      {view === "main" && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key="main"
                        >
                          <div className="flex flex-col space-y-1">
                            <button
                              className="flex items-center justify-between rounded-lg px-4 py-4 font-bold text-lg tracking-wide hover:bg-white/5"
                              onClick={handleGoToProducts}
                              type="button"
                            >
                              <span>{t("menu.products").toUpperCase()}</span>
                              <ChevronRightIcon className="h-5 w-5 text-white/30" />
                            </button>
                            {[
                              {
                                href: "/about-us",
                                label: t("menu.about").toUpperCase(),
                              },
                              {
                                href: "/gallery",
                                label: t("menu.gallery").toUpperCase(),
                              },
                            ].map((item) => (
                              <Link
                                className="rounded-lg px-4 py-4 font-bold text-lg tracking-wide hover:bg-white/5"
                                href={item.href}
                                key={item.href}
                                onClick={closeMenu}
                              >
                                {item.label}
                              </Link>
                            ))}
                            <button
                              className="flex items-center justify-between rounded-lg px-4 py-4 font-bold text-lg tracking-wide hover:bg-white/5"
                              onClick={handleGoToDownloads}
                              type="button"
                            >
                              <span>{t("menu.downloads").toUpperCase()}</span>
                              <ChevronRightIcon className="h-5 w-5 text-white/30" />
                            </button>
                            {[
                              {
                                href: "/news",
                                label: t("menu.news").toUpperCase(),
                              },
                              {
                                href: "/contact",
                                label: t("menu.contact").toUpperCase(),
                              },
                            ].map((item) => (
                              <Link
                                className="rounded-lg px-4 py-4 font-bold text-lg tracking-wide hover:bg-white/5"
                                href={item.href}
                                key={item.href}
                                onClick={closeMenu}
                              >
                                {item.label}
                              </Link>
                            ))}
                            <div className="my-2 h-px bg-white/10" />
                            <button
                              className="flex items-center justify-between rounded-lg px-4 py-4 font-bold text-lg tracking-wide hover:bg-white/5"
                              onClick={handleGoToLocales}
                              type="button"
                            >
                              <div className="flex items-center space-x-3">
                                <Image
                                  alt={lang}
                                  className="rounded-full"
                                  height={24}
                                  src={`/img/locales/${lang}.svg`}
                                  width={24}
                                />
                                <span className="uppercase">{lang}</span>
                              </div>
                              <ChevronRightIcon className="h-5 w-5 text-white/30" />
                            </button>
                          </div>
                        </motion.div>
                      )}

                      {/* ── Level 2: Product categories ── */}
                      {view === "products" && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key="products"
                        >
                          <BackButton
                            label={t("menu.products").toUpperCase()}
                            onClick={goBack}
                          />
                          <div className="flex flex-col space-y-0.5">
                            {productCategories.map((cat) => {
                              const handleSelectCategory = () =>
                                selectCategory(cat.id);
                              return (
                                <button
                                  className="flex items-center justify-between rounded-lg px-4 py-3 font-semibold text-base transition-colors hover:bg-white/5"
                                  key={cat.id}
                                  onClick={handleSelectCategory}
                                  type="button"
                                >
                                  <span>{cat.name}</span>
                                  <ChevronRightIcon className="h-5 w-5 text-white/30" />
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}

                      {/* ── Level 3: Groups within a category ── */}
                      {view === "category" && selectedCategory && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key={`category-${selectedCategoryId}`}
                        >
                          <BackButton
                            label={selectedCategory.name}
                            onClick={goBack}
                          />
                          <div className="flex flex-col space-y-0.5">
                            {selectedCategory.groups.map((group, idx) => {
                              const handleSelectGroup = () => selectGroup(idx);
                              return (
                                <button
                                  className="flex items-center justify-between rounded-lg px-4 py-3 font-semibold text-base transition-colors hover:bg-white/5"
                                  key={group.name}
                                  onClick={handleSelectGroup}
                                  type="button"
                                >
                                  <span>{group.name}</span>
                                  <ChevronRightIcon className="h-5 w-5 text-white/30" />
                                </button>
                              );
                            })}
                          </div>
                        </motion.div>
                      )}

                      {/* ── Level 4: Items within a group ── */}
                      {view === "group" && selectedGroup && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key={`group-${selectedCategoryId}-${selectedGroupIndex}`}
                        >
                          <BackButton
                            label={selectedGroup.name}
                            onClick={goBack}
                          />
                          <div className="flex flex-col">
                            {selectedGroup.subgroups
                              ? selectedGroup.subgroups.map((sub) => (
                                  <div className="mb-4" key={sub.name}>
                                    <div className="mb-1 border-white/10 border-b px-4 pb-1.5 font-bold text-[#c1a166] text-[10px] uppercase tracking-widest">
                                      {sub.name}
                                    </div>
                                    {sub.items.map((item) => (
                                      <ItemLink
                                        closeMenu={closeMenu}
                                        item={item}
                                        key={item.name}
                                      />
                                    ))}
                                  </div>
                                ))
                              : selectedGroup.items?.map((item) => (
                                  <ItemLink
                                    closeMenu={closeMenu}
                                    item={item}
                                    key={item.name}
                                  />
                                ))}
                          </div>
                        </motion.div>
                      )}

                      {/* ── Downloads ── */}
                      {view === "downloads" && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key="downloads"
                        >
                          <BackButton
                            label={t("menu.downloads").toUpperCase()}
                            onClick={goBack}
                          />
                          <div className="flex flex-col space-y-0.5">
                            {[
                              {
                                href: "/certificates",
                                name: t("downloads.certificates"),
                              },
                              {
                                href: "/application",
                                name: t("downloads.program"),
                              },
                              {
                                href: "/documents",
                                name: t("downloads.documents"),
                              },
                              {
                                href: "/advertising-materials",
                                name: t("downloads.advertising"),
                              },
                            ].map((item) => (
                              <Link
                                className="rounded-lg px-4 py-3 font-medium text-base transition-colors hover:bg-white/5"
                                href={item.href}
                                key={item.name}
                                onClick={closeMenu}
                              >
                                {item.name}
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* ── Language selector ── */}
                      {view === "locales" && (
                        <motion.div
                          {...slideProps}
                          className="p-4"
                          custom={direction}
                          key="locales"
                        >
                          <BackButton label="JĘZYK" onClick={goBack} />
                          <div className="flex flex-col space-y-1">
                            {languages.map((item) => (
                              <Link
                                className={classNames(
                                  "flex items-center space-x-4 rounded-lg px-4 py-4 font-medium text-base transition-colors",
                                  item.locale === lang
                                    ? "bg-[#c1a166] text-white"
                                    : "hover:bg-white/5"
                                )}
                                href={item.href}
                                key={item.locale}
                                onClick={closeMenu}
                              >
                                <Image
                                  alt={item.locale}
                                  className="rounded-full"
                                  height={24}
                                  src={`/img/locales/${item.locale}.svg`}
                                  width={24}
                                />
                                <span className="uppercase">{item.name}</span>
                              </Link>
                            ))}
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Footer */}
                  <div className="shrink-0 border-white/10 border-t bg-white/[0.02] p-6">
                    <Link
                      className="block rounded-full border border-white/20 px-6 py-3 text-center font-bold text-xs tracking-widest transition-all hover:border-[#c1a166] hover:text-[#c1a166]"
                      href="/distribution-network"
                    >
                      {t("menu.distributionNetwork").toUpperCase()}
                    </Link>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

function BackButton({
  label,
  onClick,
}: {
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      className="mb-4 flex items-center space-x-2 text-[#c1a166]"
      onClick={onClick}
      type="button"
    >
      <ChevronLeftIcon className="h-5 w-5" />
      <span className="font-bold text-sm tracking-widest">{label}</span>
    </button>
  );
}

function ItemLink({
  item,
  closeMenu,
}: {
  item: { name: string; href: string; isNew?: boolean };
  closeMenu: () => void;
}) {
  return (
    <Link
      className="flex items-center justify-between rounded-lg px-4 py-2.5 text-sm text-white/80 transition-colors hover:bg-white/5 hover:text-white"
      href={item.href}
      onClick={closeMenu}
    >
      <span>{item.name}</span>
      {item.isNew && (
        <span className="rounded bg-[#c1a166] px-1.5 py-0.5 font-black text-[#051124] text-[9px]">
          NEW
        </span>
      )}
    </Link>
  );
}
